// @ts-check
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  countCatalogChanges,
  getCatalogPositions,
  getDirectlyMoved,
  getOrderItemKey,
  moveCategoryItem,
  reorderCatalogItems,
  reorderCategorySubcategories,
  sortCatalogData,
  toggleSubcategoryVisible,
} from '../utils/productCatalogUtils'
import {useProductCatalogData} from './useProductCatalogData'
import {usePublishProductCatalog} from './usePublishProductCatalog'

const emptyCatalog = () =>
  /** @type {import('@/types/productCatalogTypes').ProductCategoriesData} */ ({brands: [], categories: []})

/**
 * @returns {{
 *   brands: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *   categories: import('@/types/productCatalogTypes').ProductCategory[],
 *   isLoading: boolean,
 *   isError: boolean,
 *   refetch: () => Promise<unknown>,
 *   isEditMode: boolean,
 *   isPublishing: boolean,
 *   changedItemsCount: number,
 *   hasPendingInput: boolean,
 *   hasValidationErrors: boolean,
 *   hasUnsavedChanges: boolean,
 *   setBrands: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategoryBrand[]>>,
 *   setCategories: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategory[]>>,
 *   handleBrandsReorder: (event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   handleCategoriesReorder: (event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   handleSubcategoriesReorder: (categoryId: string, event: import('@dnd-kit/dom').DragEndEvent) => void,
 *   enterEditMode: () => void,
 *   moveItem: (id: string, position: number, parentId?: string | null) => void,
 *   getOriginalPosition: (id: string, parentId?: string | null) => number,
 *   directlyMoved: Set<string>,
 *   toggleSubcategoryVisibility: (categoryId: string, subcategoryId: string) => void,
 *   onInputStateChange: (key: string, dirty: boolean, invalid: boolean) => void,
 *   discardChanges: () => void,
 *   saveChanges: () => Promise<void>,
 * }}
 */
export const useProductCatalog = () => {
  const {data, isLoading, isError, refetch} = useProductCatalogData()
  const {publishProductCatalog, isPublishing} = usePublishProductCatalog()
  const [brands, setBrands] = useState(/** @type {import('@/types/productCatalogTypes').ProductCategoryBrand[]} */ ([]))
  const [categories, setCategories] = useState(
    /** @type {import('@/types/productCatalogTypes').ProductCategory[]} */ ([])
  )
  const [baseline, setBaseline] = useState(emptyCatalog)
  const [isEditMode, setEditMode] = useState(false)
  // Category order captured when Edit Order starts; per-item undo restores positions from it.
  const [orderBaseline, setOrderBaseline] = useState(/** @type {typeof categories} */ ([]))
  const [directlyMoved, setDirectlyMoved] = useState(() => new Set())
  // Dirty/invalid flags reported by the position inputs, keyed by item.
  const [inputStates, setInputStates] = useState({})

  useEffect(() => {
    if (!data) {
      return
    }

    const sortedCatalogData = sortCatalogData(data)

    setBrands(sortedCatalogData.brands)
    setCategories(sortedCatalogData.categories)
    setBaseline(sortedCatalogData)
    setEditMode(false)
    setOrderBaseline([])
    setDirectlyMoved(new Set())
  }, [data])

  const handleBrandsReorder = useCallback(event => {
    setBrands(prev => reorderCatalogItems(prev, event))
  }, [])

  const handleCategoriesReorder = useCallback(event => {
    setCategories(prev => reorderCatalogItems(prev, event))
  }, [])

  const handleSubcategoriesReorder = useCallback((categoryId, event) => {
    setCategories(prev => reorderCategorySubcategories(prev, categoryId, event))
  }, [])

  // Visibility and order are edited in separate modes.
  const toggleSubcategoryVisibility = useCallback(
    (categoryId, subcategoryId) => {
      if (isEditMode) return
      setCategories(prev => toggleSubcategoryVisible(prev, categoryId, subcategoryId))
    },
    [isEditMode]
  )

  const enterEditMode = useCallback(() => {
    setEditMode(true)
    setOrderBaseline(categories)
    setDirectlyMoved(new Set())
  }, [categories])

  const moveItem = useCallback(
    (id, position, parentId = null) => {
      if (!isEditMode) return
      const nextCategories = moveCategoryItem(categories, id, position, parentId)
      if (nextCategories === categories) return
      setCategories(nextCategories)
      setDirectlyMoved(previous => getDirectlyMoved(previous, id, parentId, orderBaseline, nextCategories))
    },
    [isEditMode, categories, orderBaseline]
  )

  const originalPositions = useMemo(() => getCatalogPositions(orderBaseline), [orderBaseline])
  const getOriginalPosition = useCallback(
    (/** @type {string} */ id, /** @type {string | null} */ parentId = null) =>
      originalPositions.get(getOrderItemKey(id, parentId)) ?? 0,
    [originalPositions]
  )

  // Inputs with nothing to report (clean and valid, or unmounted) are dropped.
  const onInputStateChange = useCallback((key, dirty, invalid) => {
    setInputStates(previous => {
      if (!dirty && !invalid) {
        if (!(key in previous)) return previous
        const next = {...previous}
        delete next[key]
        return next
      }
      return previous[key]?.dirty === dirty && previous[key]?.invalid === invalid
        ? previous
        : {...previous, [key]: {dirty, invalid}}
    })
  }, [])

  const discardChanges = useCallback(() => {
    // Brand images are saved on their own, so discarding keeps them.
    const images = new Map(brands.map(brand => [brand.id, brand.imageUrl]))
    const restored = {
      ...baseline,
      brands: baseline.brands.map(brand => ({...brand, imageUrl: images.get(brand.id)})),
    }
    setBrands(restored.brands)
    setCategories(restored.categories)
    setBaseline(restored)
    setEditMode(false)
    setDirectlyMoved(new Set())
    setInputStates({})
  }, [baseline, brands])

  const draft = {brands, categories}
  const changedItemsCount = countCatalogChanges(baseline, draft)
  const hasPendingInput = Object.values(inputStates).some(input => input.dirty)
  const hasValidationErrors = Object.values(inputStates).some(input => input.invalid)

  const saveChanges = async () => {
    if (hasPendingInput || hasValidationErrors || changedItemsCount === 0) return
    try {
      await publishProductCatalog(draft)
      setBaseline(draft)
      setEditMode(false)
      setDirectlyMoved(new Set())
      setInputStates({})
    } catch {
      // The mutation displays the API error; the draft is kept so the user can retry.
    }
  }

  return {
    ...draft,
    isLoading,
    isError,
    refetch,
    isEditMode,
    isPublishing,
    changedItemsCount,
    hasPendingInput,
    hasValidationErrors,
    hasUnsavedChanges: changedItemsCount > 0 || hasPendingInput,
    setBrands,
    setCategories,
    handleBrandsReorder,
    handleCategoriesReorder,
    handleSubcategoriesReorder,
    enterEditMode,
    moveItem,
    getOriginalPosition,
    directlyMoved,
    toggleSubcategoryVisibility,
    onInputStateChange,
    discardChanges,
    saveChanges,
  }
}
